import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  BarChart3, Star, MessageSquare, CheckCircle, XCircle,
  TrendingUp, Users, Gift, ExternalLink, Loader2, RefreshCw
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import api from '../lib/api';

const SurveysManager = () => {
  const [surveys, setSurveys] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [surveysRes, statsRes] = await Promise.all([
        api.get(`/admin/surveys?skip=${page * 20}&limit=20`),
        api.get('/admin/surveys/stats')
      ]);
      setSurveys(surveysRes.data.surveys);
      setTotal(surveysRes.data.total);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [page]);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  const handleResend = async (reservationId) => {
    try {
      await api.post(`/admin/surveys/resend/${reservationId}`);
      toast.success('Encuesta reenviada');
      fetchData();
    } catch (error) {
      toast.error('Error al reenviar');
    }
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  const StatCard = ({ icon: Icon, label, value, subValue, color }) => (
    <div className="bg-[#1a1a1a] rounded-xl p-6">
      <div className="flex items-center gap-3 mb-2">
        <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div>
          <p className="text-gray-400 text-sm">{label}</p>
          <p className="text-2xl font-bold text-white">{value}</p>
        </div>
      </div>
      {subValue && <p className="text-gray-500 text-sm mt-2">{subValue}</p>}
    </div>
  );
  
  if (loading && !stats) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }
  
  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-[#D4AF37]" />
            Encuestas de Satisfacción
          </h2>
          <p className="text-gray-400 text-sm mt-1">
            Análisis de feedback de clientes
          </p>
        </div>
        <Button
          variant="outline"
          onClick={fetchData}
          className="border-gray-700"
          data-testid="refresh-surveys"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualizar
        </Button>
      </div>
      
      {/* Stats Grid */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            icon={Users}
            label="Total Encuestas"
            value={stats.total_surveys}
            subValue={`${stats.completed} completadas`}
            color="bg-blue-500"
          />
          <StatCard
            icon={Star}
            label="Valoración Media"
            value={`${stats.average_rating}/5`}
            subValue={`${stats.satisfaction_rate}% satisfacción`}
            color="bg-[#D4AF37]"
          />
          <StatCard
            icon={ExternalLink}
            label="Clics en Google"
            value={stats.review_clicks}
            subValue="Potenciales reseñas"
            color="bg-green-500"
          />
          <StatCard
            icon={Gift}
            label="Descuentos Enviados"
            value={stats.discounts_sent}
            subValue="Clientes fidelizados"
            color="bg-purple-500"
          />
        </div>
      )}
      
      {/* Additional Stats */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-[#1a1a1a] rounded-xl p-6 flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Respuestas positivas</p>
              <p className="text-2xl font-bold text-green-500">{stats.positive}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-500/30" />
          </div>
          <div className="bg-[#1a1a1a] rounded-xl p-6 flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Respuestas negativas</p>
              <p className="text-2xl font-bold text-red-500">{stats.negative}</p>
            </div>
            <XCircle className="w-8 h-8 text-red-500/30" />
          </div>
          <div className="bg-[#1a1a1a] rounded-xl p-6 flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Tasa de respuesta</p>
              <p className="text-2xl font-bold text-[#D4AF37]">{stats.response_rate}%</p>
            </div>
            <TrendingUp className="w-8 h-8 text-[#D4AF37]/30" />
          </div>
        </div>
      )}
      
      {/* Surveys Table */}
      <div className="bg-[#1a1a1a] rounded-xl overflow-hidden">
        <div className="p-4 border-b border-gray-800">
          <h3 className="text-lg font-semibold text-white">Últimas Encuestas</h3>
        </div>
        
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
          </div>
        ) : surveys.length === 0 ? (
          <div className="text-center py-12">
            <MessageSquare className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">No hay encuestas aún</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-800">
                  <th className="text-left p-4 text-gray-400 font-medium">Cliente</th>
                  <th className="text-left p-4 text-gray-400 font-medium">Reserva</th>
                  <th className="text-left p-4 text-gray-400 font-medium">Valoración</th>
                  <th className="text-left p-4 text-gray-400 font-medium">Resultado</th>
                  <th className="text-left p-4 text-gray-400 font-medium">Reseña</th>
                  <th className="text-left p-4 text-gray-400 font-medium">Descuento</th>
                  <th className="text-left p-4 text-gray-400 font-medium">Fecha</th>
                  <th className="text-right p-4"></th>
                </tr>
              </thead>
              <tbody>
                {surveys.map((survey) => (
                  <tr
                    key={survey.id}
                    className="border-b border-gray-800/50 hover:bg-[#252525] transition-colors"
                    data-testid={`survey-row-${survey.id}`}
                  >
                    <td className="p-4">
                      <div>
                        <p className="text-white font-medium">{survey.customer_name}</p>
                        <p className="text-gray-500 text-sm">{survey.customer_email}</p>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="font-mono text-[#D4AF37]">{survey.reservation_code}</span>
                    </td>
                    <td className="p-4">
                      {survey.rating ? (
                        <div className="flex items-center gap-1">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                              key={star}
                              className={`w-4 h-4 ${
                                star <= survey.rating
                                  ? 'fill-[#D4AF37] text-[#D4AF37]'
                                  : 'text-gray-600'
                              }`}
                            />
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-500">-</span>
                      )}
                    </td>
                    <td className="p-4">
                      {survey.result_type === 'POSITIVE' ? (
                        <Badge className="bg-green-500/20 text-green-500 border-green-500/30">
                          Positivo
                        </Badge>
                      ) : survey.result_type === 'NEGATIVE' ? (
                        <Badge className="bg-red-500/20 text-red-500 border-red-500/30">
                          Negativo
                        </Badge>
                      ) : (
                        <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/30">
                          Pendiente
                        </Badge>
                      )}
                    </td>
                    <td className="p-4">
                      {survey.review_click ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      ) : (
                        <XCircle className="w-5 h-5 text-gray-600" />
                      )}
                    </td>
                    <td className="p-4">
                      {survey.discount_sent ? (
                        <div>
                          <CheckCircle className="w-5 h-5 text-green-500 inline mr-1" />
                          <span className="text-green-500 text-sm">{survey.discount_code}</span>
                        </div>
                      ) : (
                        <XCircle className="w-5 h-5 text-gray-600" />
                      )}
                    </td>
                    <td className="p-4 text-gray-400 text-sm">
                      {formatDate(survey.created_at)}
                    </td>
                    <td className="p-4 text-right">
                      {survey.status === 'PENDING' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleResend(survey.reservation_id)}
                          className="text-gray-400 hover:text-white"
                          data-testid={`resend-survey-${survey.id}`}
                        >
                          <RefreshCw className="w-4 h-4" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Pagination */}
        {total > 20 && (
          <div className="flex justify-between items-center p-4 border-t border-gray-800">
            <Button
              variant="outline"
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              className="border-gray-700"
            >
              Anterior
            </Button>
            <span className="text-gray-400">
              Página {page + 1} de {Math.ceil(total / 20)}
            </span>
            <Button
              variant="outline"
              onClick={() => setPage(p => p + 1)}
              disabled={(page + 1) * 20 >= total}
              className="border-gray-700"
            >
              Siguiente
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SurveysManager;
